import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiTool, FiBriefcase, FiCheckCircle, FiClock, FiPhone, FiMessageCircle, FiTrendingUp } = FiIcons;

const MaintenanceArticle = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags - Optimized for local areas
    const seoData = {
      es: {
        title: 'Mantenimiento Informático Empresas San Javier, Cartagena y La Manga | Planes Anuales',
        description: 'Servicio de mantenimiento informático para empresas en San Javier, Cartagena y La Manga del Mar Menor. Planes anuales desde 300€. Soporte 24/7, backups, seguridad. ☎️ 642 643 499'
      },
      en: {
        title: 'IT Maintenance for Companies San Javier, Cartagena & La Manga | Annual Plans',
        description: 'IT maintenance service for companies in San Javier, Cartagena and La Manga del Mar Menor. Annual plans from 300€. 24/7 support, backups, security. ☎️ 642 643 499'
      },
      it: {
        title: 'Manutenzione Informatica Aziende San Javier, Cartagena e La Manga | Piani Annuali',
        description: 'Servizio di manutenzione informatica per aziende a San Javier, Cartagena e La Manga del Mar Menor. Piani annuali da 300€. Supporto 24/7, backup, sicurezza. ☎️ 642 643 499'
      }
    };

    document.title = seoData[language].title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = seoData[language].description;

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = seoData[language].title;

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = seoData[language].description;
  }, [language]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <SafeIcon icon={FiBriefcase} className="w-5 h-5" />
              <span className="font-semibold">{t('articles.maintenance.category')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('articles.maintenance.title')}
            </h1>
            <p className="text-xl text-green-100 mb-8">
              {t('articles.maintenance.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{t('articles.readTime')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                <span>{t('articles.updated')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8"
      >
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-green-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('articles.cta.business')}</h3>
                <p className="text-sm text-gray-600">{t('articles.cta.plans')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499', '_blank')}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>WhatsApp</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = 'tel:+34642643499'}
                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>{t('articles.cta.call')}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt={t('articles.maintenance.title')}
            className="w-full h-96 object-cover rounded-2xl mb-8 shadow-lg"
          />

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.maintenance.intro.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('articles.maintenance.intro.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('articles.maintenance.intro.p2')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.maintenance.benefits.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {['benefit1', 'benefit2', 'benefit3', 'benefit4', 'benefit5', 'benefit6'].map((key) => (
                <div key={key} className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {t(`articles.maintenance.benefits.${key}.title`)}
                    </h3>
                    <p className="text-gray-700 text-sm">
                      {t(`articles.maintenance.benefits.${key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.maintenance.services.title')}
            </h2>
            <div className="space-y-6">
              {['service1', 'service2', 'service3', 'service4', 'service5'].map((key) => (
                <div key={key} className="bg-white rounded-xl p-6 shadow">
                  <h3 className="font-bold text-gray-900 mb-3 text-xl">
                    {t(`articles.maintenance.services.${key}.title`)}
                  </h3>
                  <p className="text-gray-700 mb-3">
                    {t(`articles.maintenance.services.${key}.description`)}
                  </p>
                  <ul className="space-y-2">
                    {['item1', 'item2', 'item3'].map((item) => (
                      <li key={item} className="flex items-start space-x-2">
                        <span className="text-green-600 font-bold">✓</span>
                        <span className="text-gray-600 text-sm">
                          {t(`articles.maintenance.services.${key}.${item}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.maintenance.plan.title')}
            </h2>
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl p-8">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold mb-2">{t('articles.maintenance.plan.price')}</h3>
                <p className="text-green-100">{t('articles.maintenance.plan.period')}</p>
              </div>
              <div className="space-y-3">
                {['feature1', 'feature2', 'feature3', 'feature4', 'feature5', 'feature6'].map((key) => (
                  <div key={key} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 flex-shrink-0" />
                    <span>{t(`articles.maintenance.plan.${key}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t('articles.maintenance.finalCta.title')}
            </h2>
            <p className="text-xl mb-6 text-green-100">
              {t('articles.maintenance.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499?text=Quiero%20informaci%C3%B3n%20sobre%20mantenimiento%20empresarial', '_blank')}
                className="flex items-center justify-center space-x-2 bg-white text-green-600 px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>{t('articles.maintenance.finalCta.whatsapp')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/#pricing')}
                className="flex items-center justify-center space-x-2 bg-green-700 text-white px-8 py-4 rounded-xl font-semibold border-2 border-white"
              >
                <SafeIcon icon={FiTool} className="w-5 h-5" />
                <span>{t('articles.maintenance.finalCta.pricing')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default MaintenanceArticle;